import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { TypeAnimation } from "react-type-animation";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import ModalVideo from "react-modal-video";
import { fetchHome } from "./../../redux/actions/homeAction";
import { useAppDispatch } from "./../../redux/hook";

import Navbar from "../../components/navbar";
import About from "../../components/about";
import Projects from "../../components/projects";
import Stores from "../../components/stores";
import Properties from "../../components/properties";
import Team from "../../components/team-detail";
import News from "../../components/news";
import Contact from "../../components/contact";
import Footer from "../../components/footer";
import Switcher from "../../components/switcher";
import { getVideoId } from "../../utils/utils";

export default function Home() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [isOpen, setOpen] = useState(false);
  const [homeData, setHomeData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadHome = async () => {
      const home = await dispatch(fetchHome());

      if (typeof home?.payload === "string" || home?.payload === undefined) {
        setLoading(false);
      } else {
        setLoading(false);
        setHomeData(home?.payload[0]);
      }
    };
    loadHome();
  }, [dispatch]);

  if (loading) {
    return (
      <Box
        sx={{
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          top: "50%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="external">
      <Navbar />
      <section className="relative overflow-hidden md:flex md:h-screen py-36 items-center bg-gray-50 dark:bg-slate-800">
        <div className="container relative">
          <div className="grid md:grid-cols-2 grid-cols-1 items-center gap-6">
            <div className="">
              <h5 className="font-semibold leading-normal text-[20px] mt-6">
                {homeData ? (
                  <>
                    <TypeAnimation
                      sequence={[
                        homeData?.title_01,
                        1000,
                        homeData?.title_02,
                        1000,
                        homeData?.title_03,
                        1000,
                        homeData?.title_04,
                        1000,
                        homeData?.title_05,
                        1000,
                      ]}
                      wrapper="span"
                      speed={10}
                      className="typewrite ms-3"
                      repeat={Infinity}
                    />
                  </>
                ) : (
                  <>""</>
                )}
              </h5>
              <h5 className="md:text-5xl md:leading-normal text-3xl leading-normal font-bold mt-3">
                {homeData?.title}
              </h5>
              <p className="text-slate-400 dark:text-white/60 text-[17px] max-w-xl mt-4">
                {homeData?.sub_title}
              </p>
              <div className="relative mt-6">
                <Link
                  to="/aboutus"
                  className="py-2 px-5 inline-flex items-center font-medium tracking-wide border align-middle duration-500 text-[15px] text-center bg-orange-500 hover:bg-orange-600 border-orange-500 hover:border-orange-600 text-white rounded-xl"
                >
                  {t("external.home.read_more")}{" "}
                  <i className="mdi mdi-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
            <div className="relative lg:ms-6 before:content-[''] before:absolute before:start-1/2 before:-translate-x-1/2 rtl:before:translate-x-1/2 before:bottom-1/2 before:translate-y-1/2 before:w-[48rem] before:h-[20rem] before:rounded-[18rem] ltr:before:rotate-[135deg] rtl:before:rotate-[45deg] before:bg-orange-500 dark:before:bg-orange-500">
              <div className="relative z-1">
                {/* <img src={bannerImg} alt="" /> */}
                <img
                  src={
                    process.env.REACT_APP_API_URL +
                    "/wwwroot/images/" +
                    homeData?.image_file
                  }
                  alt=""
                />
                <div className="absolute bottom-2/4 translate-y-2/4 end-0 start-0 text-center">
                  <Link
                    to="#!"
                    onClick={() => setOpen(true)}
                    className="lightbox h-20 w-20 rounded-full shadow-lg shadow-slate-100 dark:shadow-slate-800 inline-flex items-center justify-center bg-white dark:bg-slate-950 text-orange-500"
                  >
                    <i className="mdi mdi-play inline-flex items-center justify-center text-2xl"></i>
                  </Link>
                </div>
                <ModalVideo
                  channel="youtube"
                  youtube={{ mute: 0, autoplay: 0 }}
                  isOpen={isOpen}
                  videoId={getVideoId(homeData?.video)}
                  onClose={() => setOpen(false)}
                />
                <div className="absolute lg:bottom-20 -bottom-5 md:-start-10 -start-2 p-4 rounded-lg shadow-md dark:shadow-gray-800 bg-white dark:bg-slate-900 m-3 w-44 text-center">
                  <span className="text-3xl font-medium mb-0">
                    <CountUp end={125} className="counter-value font-bold" />+
                  </span>
                  <h6 className="text-sm text-slate-400 mt-1">
                    {" "}
                    {t("external.home.associates")}
                  </h6>
                </div>
                <div className="absolute md:top-20 top-32 md:-end-10 -end-2 p-4 rounded-lg shadow-md dark:shadow-gray-800 bg-white dark:bg-slate-900 m-3 w-44 text-center">
                  <h6 className="font-semibold">{t("external.home.stores")}</h6>
                  <h6 className="text-sm text-slate-400 mt-1">
                    {t("external.home.15stores")}
                  </h6>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 md:col-span-6">
            <div className="lg:ms-5">
              <p className="text-orange-500 dark:text-white/60 max-w-xl">
                {homeData?.content}
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="relative overflow-hidden md:py-24 py-16">
        <About />
        <Projects pagination={false} />
        <Properties pagination={false} />
        <Stores
          containerClass="container relative md:mt-24 mt-16"
          griedClass="grid lg:grid-cols-4 md:grid-cols-3 grid-cols-1 mt-4 gap-[30px]"
        />
        <Team pagination={false} />
        <News />
        <Contact />
      </section>
      <Footer />
      <Switcher />
    </div>
  );
}
